import {useTranslation} from 'react-i18next';
import {Modal} from '../../../../../../components/modal/modal';
import {translations} from '../../../../../../i18n/translations';
import FlatInput from '../../../../../../components/flat-input/flat-input';
import {Role} from '../../../../../../backend/interfaces/role';
import {useEffect, useState} from 'react';
import {FlatMultiTextSelect} from '../../../../../../components/flat-multi-text-select/flat-multi-text-select';
import {Box} from '@mui/material';
import Switch from '../../../../../../components/switch/switch';
import {Option} from '../../../../../../components/select-dropdown/select-dropdown';
import {
  EntityOccurrenceType,
  EntityOccurrenceTypeCategory,
} from '../../../../../../backend/responses/entity-occurrence';
import {OCCURRENCES_TYPES_ALL_PROFILES_ID} from '../../../../../../utils/constants/api';

type EditOccurrenceModalProps = {
  roles: Role[];
  rolesIndex: {
    [id: string]: Role;
  };
  submitting: boolean;
  id?: string;
  occurrencesData: EntityOccurrenceType[];
  category: EntityOccurrenceTypeCategory;
  onClose: () => void;
  onSave: (
    name: string,
    category: EntityOccurrenceTypeCategory,
    active: boolean,
    action: boolean,
    notify?: string[],
    id?: string
  ) => void;
};

export default function EditOccurrenceTypeModal({
  roles,
  rolesIndex,
  submitting,
  id,
  occurrencesData,
  category,
  onClose,
  onSave,
}: EditOccurrenceModalProps) {
  const {t} = useTranslation();
  const [name, setName] = useState<string>('');
  const [active, setActive] = useState<boolean>(false);
  const [action, setAction] = useState<boolean>(false);
  const [notify, setNotify] = useState<string[] | undefined>(undefined);
  const [errorName, setErrorName] = useState<string>();
  const [editOccurrence, setEditOccurrence] = useState<EntityOccurrenceType>();

  useEffect(() => {
    if (id) {
      const find = occurrencesData.find((occurrence) => occurrence.occurrenceType.id === id);
      if (find) {
        setEditOccurrence(find);
        setName(find?.occurrenceType.name || '');
        setActive(find?.summary?.isActive || false);
        setAction(find?.summary?.actionRequired || false);

        if (find?.summary?.notifyRolesIds.includes(OCCURRENCES_TYPES_ALL_PROFILES_ID)) {
          setNotify(Object.keys(rolesIndex));
        } else {
          setNotify(find?.summary?.notifyRolesIds || []);
        }
      }
    }
  }, [id, occurrencesData, rolesIndex]);

  return (
    <Modal
      open
      maxWidth="sm"
      fullWidth
      preventCloseClick
      title={
        !id
          ? `${t(translations.office.professional_caregiver.settings.occurrences.add_modal_title)}${t(translations.office.professional_caregiver.settings.occurrences[category]).toLowerCase()}`
          : `${t(translations.office.professional_caregiver.settings.templates.edit)} "${editOccurrence?.occurrenceType.name}"`
      }
      onClose={onClose}
      className="occurrences-add-modal"
      actions={[
        {
          label: t(translations.modal.cancel),
          type: 'destructive',
          onClick: onClose,
          disabled: submitting,
        },
        {
          label: t(translations.device_form.save_button),
          type: 'nondestructive',
          onClick: () => {
            const notifyUsers = notify?.includes(OCCURRENCES_TYPES_ALL_PROFILES_ID)
              ? notify.filter((item) => item === OCCURRENCES_TYPES_ALL_PROFILES_ID)
              : notify;
            if (editOccurrence && id) {
              onSave(name, category, active, action, notifyUsers, id);
            } else {
              onSave(name, category, active, action, notifyUsers);
            }
          },
          disabled: !!errorName || name === '' || submitting,
          loading: submitting,
        },
      ]}
    >
      <FlatInput
        className="occurrences-add-modal-name"
        required
        label={
          editOccurrence && !editOccurrence?.occurrenceType.entityId
            ? `${t(translations.office.professional_caregiver.settings.occurrences.add_modal_name)} (${t(translations.office.professional_caregiver.settings.occurrences.default)})`
            : t(translations.office.professional_caregiver.settings.occurrences.add_modal_name)
        }
        value={name}
        counter={{current: name.length, max: 35}}
        onChange={(event) => {
          if (
            occurrencesData.map((obj) => obj.occurrenceType.name).includes(event.target.value) &&
            editOccurrence?.occurrenceType.name !== event.target.value
          ) {
            setErrorName(
              t(translations.office.professional_caregiver.settings.occurrences.add_name_error)
            );
          } else if (event.target.value.length >= 35) {
            setErrorName(
              t(translations.office.professional_caregiver.settings.occurrences.name_error, {
                max: 35,
              })
            );
          } else {
            setErrorName(undefined);
          }
          setName(event.target.value);
        }}
        disabled={editOccurrence && !editOccurrence?.occurrenceType.entityId}
        errors={errorName}
        disabledTooltip={t(
          translations.office.professional_caregiver.settings.occurrences.edit_name_default
        )}
      />
      <Box className="occurrences-add-modal-switch-container">
        <Switch
          label={t(translations.office.professional_caregiver.settings.occurrences.active)}
          value={active}
          onChange={(event, checked) => setActive(checked)}
        />
        <Switch
          label={t(translations.office.professional_caregiver.settings.occurrences.action_required)}
          value={action}
          onChange={(event, checked) => setAction(checked)}
        />
      </Box>
      <Box className="occurrences-add-modal-notify">
        <FlatMultiTextSelect
          multiple
          label={t(translations.office.professional_caregiver.settings.occurrences.notify)}
          onChange={(selected) => {
            const findAll = (selected as Option[]).find(
              (item) => item.key === OCCURRENCES_TYPES_ALL_PROFILES_ID
            );
            if (findAll && !notify?.includes(OCCURRENCES_TYPES_ALL_PROFILES_ID)) {
              // all profiles was checked
              setNotify(roles.map((role) => role.id || ''));
            } else if (!findAll && notify?.includes(OCCURRENCES_TYPES_ALL_PROFILES_ID)) {
              // all profiles was unchecked
              setNotify([]);
            } else if (
              notify?.includes(OCCURRENCES_TYPES_ALL_PROFILES_ID) &&
              notify.length !== (selected as Option[]).length
            ) {
              // other profiles was unchecked - remove all profiles check
              setNotify(
                (selected as Option[])
                  .filter((item) => item.key !== OCCURRENCES_TYPES_ALL_PROFILES_ID)
                  .map((item) => item.key)
              );
            } else if (!findAll && (selected as Option[]).length === roles?.length - 1) {
              // all other profiles are selected - check all profiles
              setNotify([
                ...(selected as Option[]).map((item) => item.key),
                OCCURRENCES_TYPES_ALL_PROFILES_ID,
              ]);
            } else {
              setNotify((selected as Option[]).map((item) => item.key));
            }
          }}
          value={
            notify?.map((notify) => ({
              key: notify,
              label: rolesIndex[notify]?.name || notify,
            })) || []
          }
          options={
            !roles
              ? []
              : roles.map((role, index) => ({
                  key: role.id || index.toString(),
                  label: role.name,
                }))
          }
        />
      </Box>
    </Modal>
  );
}
